/* Drill into the existing Shopify menu without leaving the open Shop panel. */
(() => {
  const controllers = new WeakMap();
  const menus = new Set();
  const searchControllers = new WeakMap();
  const searches = new Set();
  const cartControllers = new WeakMap();
  const carts = new Set();
  const desktop = window.matchMedia('(min-width: 1000px)');
  const inlineSearch = window.matchMedia('(min-width: 641px)');
  let backdrop;
  const syncBackdrop = () => {
    const searchOpen = inlineSearch.matches && [...searches].some((search) =>
      search.isConnected && search.classList.contains('is-visible'));
    const cartOpen = [...carts].some((cart) =>
      cart.isConnected && cart.getAttribute('aria-hidden') === 'false');
    const open = searchOpen || cartOpen || (desktop.matches && [...menus].some((menu) =>
      menu.isConnected && menu.getAttribute('aria-hidden') === 'false'));
    if (!backdrop && open) {
      backdrop = document.createElement('div');
      backdrop.className = 'dsn-menu-backdrop';
      backdrop.setAttribute('aria-hidden', 'true');
      // Existing outside handlers close menus/search. Consume the gesture
      // here so it cannot activate content underneath during the fade.
      backdrop.addEventListener('pointerdown', (event) => {
        event.preventDefault();
        // Use the native cart toggle so its focus trap and scroll lock release
        // together, with focus restored to the nav trigger.
        const openCart = [...carts].find((cart) => cart.getAttribute('aria-hidden') === 'false');
        openCart?.closest('.dsn-header-section')?.querySelector('.header__cart-toggle[data-action="toggle-mini-cart"]')?.click();
        const focusedMenu = [...menus].find((menu) => menu.contains(document.activeElement));
        focusedMenu?.previousElementSibling?.focus({ preventScroll: true });
        const focusedSearch = [...searches].find((search) => search.contains(document.activeElement));
        focusedSearch?.closest('.dsn-header-section')?.querySelector('[data-action="toggle-search"]')?.focus({ preventScroll: true });
      });
      document.body.append(backdrop);
      // Give the closed opacity a frame before the first opening transition.
      requestAnimationFrame(() => requestAnimationFrame(syncBackdrop));
      return;
    }
    backdrop?.classList.toggle('is-search-open', searchOpen);
    backdrop?.classList.toggle('is-cart-open', cartOpen);
    backdrop?.classList.toggle('is-open', open);
  };
  desktop.addEventListener('change', syncBackdrop);
  inlineSearch.addEventListener('change', syncBackdrop);
  const initialize = (root) => {
    root.querySelectorAll('.dsn-header .dsn-mini-cart').forEach((cart) => {
      if (cartControllers.has(cart)) return;
      // Warehouse retains ownership of opening, cart refreshes and dismissal.
      const observer = new MutationObserver(syncBackdrop);
      observer.observe(cart, { attributes: true, attributeFilter: ['aria-hidden'] });
      carts.add(cart);
      syncBackdrop();
      cartControllers.set(cart, () => {
        observer.disconnect(); carts.delete(cart); syncBackdrop();
      });
    });
    root.querySelectorAll('.dsn-header .header__search-bar-wrapper').forEach((search) => {
      if (searchControllers.has(search)) return;
      // Observe Warehouse's existing open state; its native controller and the
      // header helper retain ownership of search, dismissal and focus.
      const observer = new MutationObserver(syncBackdrop);
      observer.observe(search, { attributes: true, attributeFilter: ['class'] });
      searches.add(search);
      syncBackdrop();
      searchControllers.set(search, () => {
        observer.disconnect(); searches.delete(search); syncBackdrop();
      });
    });
    root.querySelectorAll('[data-dsn-shop]').forEach((menu) => {
      if (controllers.has(menu)) return;
      const trigger = menu.previousElementSibling;
      const overview = menu.querySelector('[data-dsn-shop-overview]');
      if (!trigger || !overview) return;
      const events = new AbortController();
      const panels = [...menu.querySelectorAll('[data-dsn-shop-panel]')];
      const categories = [...menu.querySelectorAll('[data-dsn-shop-category]')];
      let selected = null;
      const reset = (restoreFocus = false) => {
        overview.hidden = false;
        if (restoreFocus) selected?.focus({ preventScroll: true });
        panels.forEach((panel) => { panel.hidden = true; });
        categories.forEach((category) => category.setAttribute('aria-expanded', 'false'));
        selected = null;
        menu.scrollTop = 0;
      };
      const close = (restoreFocus = false) => {
        if (restoreFocus) trigger.focus({ preventScroll: true });
        trigger.setAttribute('aria-expanded', 'false');
        trigger.parentElement.classList.remove('is-dropdown-open');
        menu.setAttribute('aria-hidden', 'true');
        reset();
      };
      categories.forEach((category) => {
        category.setAttribute('role', 'button');
        const select = (event) => {
          if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
          const panel = panels.find((item) => item.id === category.getAttribute('aria-controls'));
          if (!panel) return;
          event.preventDefault();
          selected = category;
          category.setAttribute('aria-expanded', 'true');
          panel.hidden = false;
          panel.querySelector('[data-dsn-shop-back]').focus({ preventScroll: true });
          overview.hidden = true;
          menu.scrollTop = 0;
        };
        category.addEventListener('click', select, { signal: events.signal });
        category.addEventListener('keydown', (event) => {
          if (event.key === ' ') select(event);
        }, { signal: events.signal });
      });
      menu.querySelectorAll('[data-dsn-shop-back]').forEach((back) => {
        back.addEventListener('click', () => reset(true), { signal: events.signal });
      });
      // Warehouse owns hover/touch opening. A click must never follow Categories.
      trigger.addEventListener('click', (event) => event.preventDefault(), { signal: events.signal });
      document.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape' || menu.getAttribute('aria-hidden') !== 'false') return;
        event.preventDefault();
        if (selected) reset(true);
        else close(true);
      }, { signal: events.signal });
      document.addEventListener('pointerdown', (event) => {
        if (!menu.contains(event.target) && !trigger.contains(event.target)) close();
      }, { signal: events.signal });
      const observer = new MutationObserver(() => {
        if (menu.getAttribute('aria-hidden') === 'true') reset();
        syncBackdrop();
      });
      observer.observe(menu, { attributes: true, attributeFilter: ['aria-hidden'] });
      menus.add(menu);
      syncBackdrop();
      controllers.set(menu, () => {
        events.abort(); observer.disconnect(); menus.delete(menu); syncBackdrop();
      });
    });
  };
  initialize(document);
  document.addEventListener('shopify:section:load', (event) => initialize(event.target.parentElement || document));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('.dsn-header .dsn-mini-cart').forEach((cart) => {
      cartControllers.get(cart)?.();
      cartControllers.delete(cart);
    });
    event.target.querySelectorAll('.dsn-header .header__search-bar-wrapper').forEach((search) => {
      searchControllers.get(search)?.();
      searchControllers.delete(search);
    });
    event.target.querySelectorAll('[data-dsn-shop]').forEach((menu) => {
      controllers.get(menu)?.();
      controllers.delete(menu);
    });
  });
})();
